import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Activity, TrendingUp, Zap, Shield, Globe, BarChart3, Bot, DollarSign, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/utils/api';
import PriceTicker from '@/components/PriceTicker';

const LandingPage = ({ setUser, user }) => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, user]);

  if (user) return null;

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await api.post(endpoint, { email, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const pricingTiers = [
    {
      name: 'Free',
      price: '$0',
      features: ['Arbitrage alerts only', 'Real-time opportunities', 'Up to 3 exchange connections', 'Email support']
    },
    {
      name: 'Pro',
      price: '$99',
      period: '/month',
      popular: true,
      features: ['Automated trading bots', 'All exchanges', 'Up to 5 active bots', 'Advanced strategies', 'Priority support']
    },
    {
      name: 'Elite',
      price: '$499',
      period: '/month',
      features: ['Unlimited bots', 'AI-powered insights', 'Custom strategies', 'Dedicated account manager', 'API access']
    },
    {
      name: 'Enterprise',
      price: '$1,999',
      period: '/month',
      features: ['White-label solution', 'Custom integrations', 'AI trading models', 'On-premise deployment', '24/7 phone support']
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <Activity className="h-8 w-8 text-primary" />
              <span className="font-heading text-2xl font-bold">ARBEX</span>
            </div>
            <Button 
              data-testid="get-started-btn"
              onClick={() => setShowAuth(true)} 
              className="bg-primary text-primary-foreground font-mono uppercase tracking-wider hover:bg-primary/90 rounded-sm"
            >
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1652416906473-8ed222d91243?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-radial from-primary/10 via-transparent to-transparent" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold mb-6">
              Professional Crypto
              <span className="block text-primary">Arbitrage Terminal</span>
            </h1>
            <p className="text-xl text-secondary-foreground max-w-3xl mx-auto mb-12">
              Automated cross-exchange arbitrage trading. Real-time opportunities across
              Binance, Coinbase, Kraken, KuCoin, and Bybit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => setShowAuth(true)}
                size="lg"
                className="bg-primary text-primary-foreground font-mono uppercase tracking-wider hover:bg-primary/90 rounded-sm text-lg px-8 py-6"
              >
                Start Trading Now
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-border hover:bg-muted text-foreground font-mono rounded-sm text-lg px-8 py-6"
              >
                View Live Opportunities
              </Button>
            </div>
          </motion.div>

          {/* Live Prices */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex justify-center mt-10"
          >
            <PriceTicker />
          </motion.div>

          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20"
          >
            {[
              { label: 'Daily Volume', value: '$2.4B', icon: DollarSign },
              { label: 'Active Bots', value: '12,453', icon: Bot },
              { label: 'Exchanges', value: '5', icon: Globe },
              { label: 'Avg ROI', value: '18.3%', icon: TrendingUp }
            ].map((stat, i) => (
              <div key={i} className="bg-card border border-border p-6 rounded-sm text-center">
                <stat.icon className="h-6 w-6 text-primary mx-auto mb-2" />
                <div className="font-mono text-3xl font-bold text-primary">{stat.value}</div>
                <div className="text-sm text-secondary-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-4xl font-bold text-center mb-16">
            Built for <span className="text-primary">Performance</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Zap,
                title: 'Low-Latency Execution',
                description: 'Sub-millisecond order execution across all exchanges with optimized routing.'
              },
              {
                icon: Shield,
                title: 'Enterprise Security',
                description: 'Bank-grade encryption for API keys. Your credentials never leave our secure vault.'
              },
              {
                icon: BarChart3,
                title: 'Real-Time Analytics',
                description: 'Track profits, analyze performance, and optimize strategies with AI-powered insights.'
              }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-card border border-border p-8 rounded-sm hover:border-primary/30 transition-all"
              >
                <feature.icon className="h-12 w-12 text-primary mb-4" />
                <h3 className="font-heading text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-secondary-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-4xl font-bold text-center mb-16">
            Choose Your <span className="text-primary">Plan</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingTiers.map((tier, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`bg-card border p-8 rounded-sm relative ${
                  tier.popular ? 'border-primary glow-green' : 'border-border'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-sm font-mono text-sm uppercase">
                    Popular
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-heading text-2xl font-bold mb-2">{tier.name}</h3>
                  <div className="font-mono text-4xl font-bold text-primary">
                    {tier.price}
                    {tier.period && <span className="text-lg text-secondary-foreground">{tier.period}</span>}
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-secondary-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => setShowAuth(true)}
                  className={`w-full font-mono uppercase tracking-wider rounded-sm ${
                    tier.popular 
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-transparent border border-border hover:bg-muted'
                  }`}
                >
                  Get Started
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Activity className="h-6 w-6 text-primary" />
            <span className="font-heading text-xl font-bold">ARBEX</span>
          </div>
          <p className="text-secondary-foreground text-sm">
            © 2025 Arbex. Professional crypto arbitrage trading platform.
          </p>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <Label htmlFor="email" className="font-mono text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-input border-border focus:ring-1 focus:ring-primary font-mono text-sm rounded-sm"
                placeholder="trader@arbex.io"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-mono text-sm">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-input border-border focus:ring-1 focus:ring-primary font-mono text-sm rounded-sm"
                placeholder="••••••••"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-wider hover:bg-primary/90 rounded-sm"
            >
              {loading ? 'Processing...' : (isLogin ? 'Login' : 'Register')}
            </Button>
            <p className="text-center text-sm text-secondary-foreground">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary hover:underline font-mono"
              >
                {isLogin ? 'Register' : 'Login'}
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;